import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  useLazyGetExternalOrdersShipmentTrackingQuery,
  useUpdateExternalOrdersBulkOrderStatusesFromDhlMutation,
} from '../../features/ordersApiSlice';
import DataTable from 'react-data-table-component';
import { extractTrackingId } from '../../utils';
import { Card, CardBody, Modal, ModalBody, Spinner } from 'reactstrap';
import { Btn } from '../../AbstractElements';
import { toast } from 'react-toastify';

const getStatusColour = (status) => {
  if (!status) return '';
  if (status === 'delivered') return 't-text-green-500';
  if (['returned', 'return-delivered', 'failure'].includes(status)) return 't-text-red-500';
  return 't-text-gray-500';
};

const computeStatusFromDhl = (trackingData) => {
  if (!trackingData?.status?.statusCode) return null;
  const isReturn = trackingData?.returnFlag === true;
  const isDelivered = trackingData?.status?.statusCode === 'delivered';
  return isReturn && isDelivered ? 'return-delivered' : isReturn ? 'returned' : trackingData.status.statusCode;
};

const ExternalOrderShipmentTracking = () => {
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const trackingIds = searchParams.get('trackingIds');
  const [localOrders, setLocalOrders] = useState(state?.ordersWithTrackingId || []);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [getShipments, { data: shipmentTrackings, isLoading }] =
    useLazyGetExternalOrdersShipmentTrackingQuery();
  const [updateOrderStatuses, { isLoading: updateOrderStatusesLoading }] =
    useUpdateExternalOrdersBulkOrderStatusesFromDhlMutation();

  useEffect(() => {
    getShipments(trackingIds);
  }, [trackingIds]);

  useEffect(() => {
    if (!shipmentTrackings?.data) return;
    setLocalOrders((prev) =>
      prev.map((order) => {
        const trackingId = extractTrackingId(order?.order_tracking_link);
        const trackingData = shipmentTrackings.data.find((item) => item?.id === trackingId);
        const newStatus = computeStatusFromDhl(trackingData);
        return newStatus ? { ...order, status: newStatus } : order;
      }),
    );
    setIsRefreshing(false);
  }, [shipmentTrackings]);

  const onRefresh = () => {
    setIsRefreshing(true);
    getShipments(trackingIds);
  };

  const tableColumns = [
    {
      name: 'Order#',
      selector: (row) => `${row?.internal_order_id}`,
      width: '150px',
    },
    {
      name: 'Name',
      selector: (row) => row?.user?.full_name,
      width: '200px',
      center: false,
    },
    {
      name: 'Email',
      selector: (row) => row?.user?.email,
      width: '200px',
      center: false,
    },
    {
      name: 'Status',
      width: '200px',
      selector: (row) => <p className={getStatusColour(row?.status)}>{row?.status}</p>,
      center: false,
    },
    {
      name: 'Tracking Id',
      width: '250px',
      selector: (row) => {
        const trackingId = extractTrackingId(row?.order_tracking_link);
        return <p>{trackingId}</p>;
      },
      center: false,
    },
    {
      name: 'Tracking Link',
      width: '150px',
      selector: (row) => (
        <a href={row?.order_tracking_link} target="_blank" rel="noreferrer" className="t-text-secondary">
          Link
        </a>
      ),
      center: false,
    },
    {
      name: 'Dhl Status',
      width: '400px',
      selector: (row) => {
        const trackingId = extractTrackingId(row?.order_tracking_link);
        const trackingData = shipmentTrackings?.data?.find((item) => item?.id === trackingId);
        return (
          <div className="t-flex t-flex-col t-gap-1 t-max-w-[400px]">
            <p className="t-font-semibold">{trackingData?.status?.statusCode}</p>
            <p className="t-whitespace-normal t-break-words">{trackingData?.status?.description}</p>
          </div>
        );
      },
      center: false,
    },
  ];

  const onUpdateStatuses = async () => {
    try {
      if (!localOrders?.length || !JSON.parse(trackingIds) || !shipmentTrackings) {
        toast.error('Data not available!');
        return;
      }
      const data = localOrders
        ?.map((item) => {
          const trackingId = extractTrackingId(item?.order_tracking_link);
          const trackingData = shipmentTrackings?.data?.find((item) => item?.id === trackingId);
          return {
            orderId: item?.id,
            status: trackingData?.status?.statusCode,
            description: trackingData?.status?.description,
            returnFlag: trackingData?.returnFlag,
          };
        })
        ?.filter((i) => i?.status);
      const response = await updateOrderStatuses({ orders: data });
      if (response?.data) {
        toast.success('Status updated successfully!');
      }
    } catch (err) {
      console.log('Error => ', err);
      toast.error(err?.message || 'Failed to update status!');
    }
  };

  const foundTrackings = useMemo(() => {
    const data = localOrders?.map((item) => {
      const trackingId = extractTrackingId(item?.order_tracking_link);
      const trackingData = shipmentTrackings?.data?.find((item) => item?.id === trackingId);
      return {
        orderId: item?.id,
        status: trackingData?.status?.statusCode,
      };
    });
    const availableTrackings = data?.filter((i) => i?.status);
    const notAvailableTrackings = data?.filter((i) => !i?.status);
    return {
      availableTrackings,
      notAvailableTrackings,
    };
  }, [shipmentTrackings, localOrders]);

  if (!state?.ordersWithTrackingId) {
    return <p>No orders found!</p>;
  } else if (!JSON.parse(trackingIds)) {
    return <p>No tracking ids provided!</p>;
  } else if (isLoading && !isRefreshing) {
    return <p>Loading...</p>;
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <h2 className="t-text-xl t-font-bold">Shipment Tracking</h2>

      <Modal isOpen={isRefreshing} centered>
        <ModalBody className="t-flex t-flex-col t-items-center t-gap-3 t-py-8">
          <Spinner color="primary" />
          <p className="t-text-sm t-text-gray-600">Fetching latest DHL data...</p>
        </ModalBody>
      </Modal>

      <Card>
        <div className="text-end t-flex t-gap-2 t-justify-end t-p-4">
          <Btn
            attrBtn={{
              className: 'mr-3',
              color: 'primary',
              disabled: isLoading || updateOrderStatusesLoading || isRefreshing,
              onClick: onRefresh,
            }}
          >
            Refresh
          </Btn>
          <Btn
            attrBtn={{
              className: 'mr-3',
              color: 'primary',
              disabled: isLoading || updateOrderStatusesLoading || isRefreshing,
              onClick: onUpdateStatuses,
            }}
          >
            Update Statuses
          </Btn>
        </div>
        <CardBody>
          <div className="t-flex t-items-center t-gap-2">
            <h3 className="t-text-lg t-font-semibold">Available Tracking</h3>
            {foundTrackings?.availableTrackings?.map((item) => (
              <p>{item?.orderId + ', '}</p>
            ))}
          </div>
          <div className="t-flex t-items-center t-gap-2">
            <h3 className="t-text-lg t-font-semibold">Not available Tracking</h3>
            {foundTrackings?.notAvailableTrackings?.map((item) => (
              <p>{item?.orderId + ', '}</p>
            ))}
          </div>
          <p className="t-text-red-500">Update status will only update the orders with available trackings</p>
          <DataTable
            columns={tableColumns}
            data={localOrders}
            striped={true}
            center={true}
            pagination
            paginationServer
            progressPending={isLoading}
            selectableRows
          />
        </CardBody>
      </Card>
    </div>
  );
};

export default ExternalOrderShipmentTracking;
